import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Form,
  required,
  useDataProvider,
  useDelete,
  useGetList,
  useGetOne,
  useNotify,
} from "ra-core";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  ArrowRight,
  Copy,
  FileText,
  Monitor,
  Pencil,
  Plus,
  Smartphone,
  Trash2,
} from "lucide-react";
import { Confirm } from "@/components/admin/confirm";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  DEFAULT_MAIL_TEMPLATES,
  MAIL_TEMPLATE_VARIABLES,
  getMissingMailVariables,
  renderMailTemplate,
  type MailTemplate,
} from "../../../../supabase/functions/_shared/mailTemplates";
import { EmailPreview } from "./EmailPreview";
import { MailAttachments } from "./MailAttachments";
import {
  validateMailAttachments,
  type MailAttachment,
} from "../../../../supabase/functions/_shared/mailAttachments";
import {
  TEMPLATE_FIELD_LABELS,
  TEMPLATE_PREVIEW_EXAMPLE,
  getUnknownTemplateFields,
} from "./templateFields";

type EditorState = { template?: MailTemplate; create: boolean };

export const EmailTemplatesPage = () => {
  const [editor, setEditor] = useState<EditorState>();
  const [using, setUsing] = useState<MailTemplate>();
  const [selected, setSelected] = useState<MailTemplate>();
  return (
    <main className="mx-auto min-w-0 w-full max-w-7xl px-4 py-6 pb-24 md:px-6 md:pb-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Шаблоны писем
          </h1>
          <p className="text-sm leading-6 text-muted-foreground">
            Подготовьте основу один раз. Выберите клиента, уточните условия и
            утвердите готовое письмо.
          </p>
        </div>
        <Button onClick={() => setEditor({ create: true })}>
          <Plus />
          Создать шаблон
        </Button>
      </header>
      <Form
        defaultValues={{ search: "", scope: "all" }}
        onSubmit={() => undefined}
      >
        <TemplateLibrary
          selected={selected}
          onSelect={setSelected}
          onEdit={setEditor}
          onUse={setUsing}
        />
      </Form>
      {editor && (
        <TemplateEditor
          key={`${editor.create}-${editor.template?.id ?? "new"}`}
          {...editor}
          onClose={() => setEditor(undefined)}
          onSaved={(template) => {
            setSelected(template);
            setEditor(undefined);
          }}
        />
      )}
      {using && (
        <UseTemplateDialog
          template={using}
          onClose={() => setUsing(undefined)}
        />
      )}
    </main>
  );
};

const TemplateLibrary = ({
  selected,
  onSelect,
  onEdit,
  onUse,
}: {
  selected?: MailTemplate;
  onSelect: (template: MailTemplate | undefined) => void;
  onEdit: (state: EditorState) => void;
  onUse: (template: MailTemplate) => void;
}) => {
  const { watch } = useFormContext<{ search: string; scope: string }>();
  const search = watch("search") || "";
  const scope = watch("scope");
  const [page, setPage] = useState(1);
  const [compact, setCompact] = useState(false);
  useEffect(() => setPage(1), [search, scope]);
  const {
    data = [],
    total = 0,
    isPending,
    error,
    refetch,
  } = useGetList<MailTemplate>(
    "email_templates",
    {
      pagination: { page, perPage: 25 },
      sort: { field: "name", order: "ASC" },
      filter: { q: search },
    },
    { enabled: scope !== "builtin" },
  );
  const builtins =
    scope === "mine"
      ? []
      : DEFAULT_MAIL_TEMPLATES.filter((item) =>
          `${item.name} ${item.subject} ${item.text}`
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase().trim()),
        );
  const entries = [...builtins, ...(scope === "builtin" ? [] : data)];
  // Keep a saved or explicitly selected template visible across pagination.
  // Filters still determine whether it belongs in the current view.
  const selectedIsBuiltin = DEFAULT_MAIL_TEMPLATES.some(
    (item) => item.id === selected?.id,
  );
  const selectedMatches =
    selected &&
    (scope === "all" || (scope === "builtin") === selectedIsBuiltin) &&
    `${selected.name} ${selected.subject} ${selected.text}`
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase().trim());
  const active =
    entries.find((item) => item.id === selected?.id) ||
    (selectedMatches ? selected : entries[0]);
  const builtIn =
    active && DEFAULT_MAIL_TEMPLATES.some((item) => item.id === active.id);
  const {
    data: fullTemplate,
    isPending: filesPending,
    error: filesError,
    refetch: retryFiles,
  } = useGetOne<MailTemplate>(
    "email_templates",
    { id: active?.id ?? 0, meta: { templateFiles: true } },
    { enabled: !!active && !builtIn },
  );
  const detailed = builtIn ? active : fullTemplate;
  const rendered = active
    ? renderMailTemplate(detailed || active, TEMPLATE_PREVIEW_EXAMPLE)
    : undefined;

  return (
    <>
      <div className="mb-6 grid max-w-2xl gap-3 sm:grid-cols-[minmax(0,1fr)_200px]">
        <TextInput
          source="search"
          label="Поиск шаблона"
          placeholder="Название или содержание"
        />
        <SelectInput
          source="scope"
          label="Показывать"
          translateChoice={false}
          choices={[
            { id: "all", name: "Все шаблоны" },
            { id: "builtin", name: "Готовые основы" },
            { id: "mine", name: "Мои шаблоны" },
          ]}
        />
      </div>
      <div className="grid min-w-0 items-start gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <section aria-label="Библиотека шаблонов" className="min-w-0 space-y-4">
          {error && scope !== "builtin" && (
            <div
              role="alert"
              className="space-y-2 rounded-md border border-destructive/30 p-3 text-sm"
            >
              <p>
                Не удалось загрузить личные шаблоны. Готовые основы доступны.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => refetch()}
              >
                Повторить
              </Button>
            </div>
          )}
          {isPending && scope !== "builtin" && (
            <p role="status" className="text-sm text-muted-foreground">
              Загрузка личных шаблонов…
            </p>
          )}
          {entries.length === 0 && (scope === "builtin" || !isPending) && (
            <div className="space-y-2 py-8 text-sm">
              <p className="font-medium">Шаблоны не найдены</p>
              <p className="text-muted-foreground">
                Измените поиск или создайте свой шаблон на основе готового
                письма.
              </p>
            </div>
          )}
          <ul className="grid max-h-80 gap-2 overflow-y-auto p-1 sm:grid-cols-2 lg:max-h-[65dvh] lg:grid-cols-1">
            {entries.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onSelect(item)}
                  aria-pressed={active?.id === item.id}
                  className={cn(
                    "w-full rounded-lg border p-4 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                    active?.id === item.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50",
                  )}
                >
                  <span className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <FileText className="size-3.5" />
                    {DEFAULT_MAIL_TEMPLATES.some((t) => t.id === item.id)
                      ? "Готовая основа"
                      : "Мой шаблон"}
                  </span>
                  <span className="block break-words text-sm font-medium">
                    {item.name}
                  </span>
                  {!!(item.attachment_count ?? item.attachments?.length) && (
                    <span className="text-xs text-muted-foreground">
                      Файлов:{" "}
                      {item.attachment_count ?? item.attachments?.length}
                    </span>
                  )}
                  <span className="mt-1 block line-clamp-2 break-words text-xs leading-5 text-muted-foreground">
                    {item.subject}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {scope !== "builtin" && total > 25 && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Назад
              </Button>
              <span>
                Мои: {page} / {Math.ceil(total / 25)}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={page * 25 >= total}
                onClick={() => setPage(page + 1)}
              >
                Далее
              </Button>
            </div>
          )}
        </section>
        {active && rendered && (
          <section className="min-w-0 space-y-4" aria-label="Выбранный шаблон">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <h2 className="break-words text-lg font-medium">
                  {active.name}
                </h2>
                <p className="text-xs leading-5 text-muted-foreground">
                  Предпросмотр на вымышленных данных. Условия задаются отдельно
                  для каждого клиента.
                </p>
              </div>
              <PreviewSize compact={compact} onChange={setCompact} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={!detailed || (!!filesError && !builtIn)}
                onClick={() => onUse(detailed!)}
              >
                Подготовить письмо
                <ArrowRight />
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!detailed || (!!filesError && !builtIn)}
                onClick={() =>
                  onEdit({ template: detailed, create: !!builtIn })
                }
              >
                <Pencil />
                {builtIn ? "Изменить копию" : "Редактировать"}
              </Button>
              {!builtIn && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!detailed || !!filesError}
                    onClick={() => onEdit({ template: detailed, create: true })}
                  >
                    <Copy />
                    Дублировать
                  </Button>
                  <DeleteTemplateButton
                    key={active.id}
                    template={active}
                    onDeleted={() => {
                      onSelect(undefined);
                      if (page > 1 && data.length === 1) setPage(page - 1);
                    }}
                  />
                </>
              )}
            </div>
            <div className="min-w-0 rounded-xl bg-muted/40 p-2 sm:p-5">
              <EmailPreview
                {...rendered}
                from="Сергей <manager@example.org>"
                to="Анна <client@example.org>"
                compact={compact}
              />
            </div>
            <TemplateFieldsSummary template={active} />
            {!builtIn && filesPending && (
              <p role="status" className="text-sm">
                Загрузка файлов шаблона…
              </p>
            )}
            {!builtIn && filesError && (
              <div role="alert" className="space-y-2 text-sm text-destructive">
                <p>
                  Не удалось загрузить файлы шаблона. Повторите загрузку перед
                  подготовкой письма.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => retryFiles()}
                >
                  Повторить загрузку файлов
                </Button>
              </div>
            )}
            {detailed && (
              <MailAttachments
                value={detailed.attachments}
                label="Файлы шаблона"
              />
            )}
          </section>
        )}
      </div>
    </>
  );
};

const DeleteTemplateButton = ({
  template,
  onDeleted,
}: {
  template: MailTemplate;
  onDeleted: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const notify = useNotify();
  const [deleteTemplate, { isPending, error, reset }] = useDelete<
    MailTemplate,
    Error
  >(
    "email_templates",
    { id: template.id, previousData: template },
    {
      mutationMode: "pessimistic",
      onSuccess: () => {
        setOpen(false);
        onDeleted();
        notify("Шаблон удалён", { type: "success" });
      },
    },
  );

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={() => setOpen(true)}
      >
        <Trash2 />
        Удалить
      </Button>
      <Confirm
        isOpen={open}
        title="Удалить шаблон?"
        content={
          <>
            <DialogDescription className="break-words">
              Шаблон «{template.name}» и его файлы будут удалены без возможности
              восстановления. Сохранённые черновики и отправленные письма
              останутся.
            </DialogDescription>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                Не удалось удалить шаблон. {error.message}
              </p>
            )}
          </>
        }
        confirm={isPending ? "Удаление…" : "Удалить шаблон"}
        cancel="Отмена"
        confirmColor="warning"
        ConfirmIcon={Trash2}
        loading={isPending}
        onConfirm={() => deleteTemplate()}
        onClose={() => {
          if (isPending) return;
          setOpen(false);
          reset();
        }}
      />
    </>
  );
};

const PreviewSize = ({
  compact,
  onChange,
}: {
  compact: boolean;
  onChange: (value: boolean) => void;
}) => (
  <div
    className="flex shrink-0 gap-1"
    role="group"
    aria-label="Размер предпросмотра"
  >
    <Button
      type="button"
      variant={compact ? "ghost" : "secondary"}
      size="icon"
      aria-label="На компьютере"
      aria-pressed={!compact}
      onClick={() => onChange(false)}
    >
      <Monitor />
    </Button>
    <Button
      type="button"
      variant={compact ? "secondary" : "ghost"}
      size="icon"
      aria-label="На телефоне"
      aria-pressed={compact}
      onClick={() => onChange(true)}
    >
      <Smartphone />
    </Button>
  </div>
);

const TemplateFieldsSummary = ({
  template,
}: {
  template: Pick<MailTemplate, "subject" | "text">;
}) => {
  const fields = getMissingMailVariables(
    `${template.subject}\n${template.text}`,
  );
  return fields.length ? (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">Поля для подстановки</p>
      <div className="flex flex-wrap gap-1.5">
        {fields.map((name) => (
          <Badge key={name} variant="secondary">
            {TEMPLATE_FIELD_LABELS[name] || name}
          </Badge>
        ))}
      </div>
    </div>
  ) : null;
};

export const TemplateEditor = ({
  template,
  create,
  onClose,
  onSaved,
}: EditorState & {
  onClose: () => void;
  onSaved: (template: MailTemplate) => void;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const dirty = useRef(false);
  const busy = useRef(false);
  const close = () => {
    if (
      !busy.current &&
      (!dirty.current ||
        window.confirm("Закрыть редактор без сохранения изменений?"))
    )
      onClose();
  };
  const defaults = {
    name: template ? `${template.name}${create ? " — моя версия" : ""}` : "",
    subject: template?.subject || "",
    text: template?.text || "",
    attachments: template?.attachments || [],
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        ref={contentRef}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus({ preventScroll: true });
        }}
        className="max-h-[94dvh] overflow-y-auto sm:max-w-6xl [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          close();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {create ? "Новый шаблон письма" : "Редактировать шаблон"}
          </DialogTitle>
          <DialogDescription>
            Редактируйте текст и сразу смотрите результат. Имена и условия
            подставятся при подготовке письма клиенту.
          </DialogDescription>
        </DialogHeader>
        <Form defaultValues={defaults} onSubmit={() => undefined}>
          <TemplateEditorForm
            template={template}
            create={create}
            onSaved={onSaved}
            onClose={close}
            dirty={dirty}
            busy={busy}
          />
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const TemplateEditorForm = ({
  template,
  create,
  onSaved,
  onClose,
  dirty,
  busy,
}: EditorState & {
  onSaved: (template: MailTemplate) => void;
  onClose: () => void;
  dirty: React.RefObject<boolean>;
  busy: React.RefObject<boolean>;
}) => {
  const { watch, setValue, trigger, getValues, formState } =
    useFormContext<
      Pick<MailTemplate, "name" | "subject" | "text" | "attachments">
    >();
  const values = watch();
  const provider = useDataProvider();
  const client = useQueryClient();
  const notify = useNotify();
  const [compact, setCompact] = useState(false);
  const [sample, setSample] = useState(true);
  const [filesBusy, setFilesBusy] = useState(false);
  const [field, setField] = useState<"subject" | "text">("text");
  const insertion = useRef<
    { start: number; end: number; field: "subject" | "text" } | undefined
  >(undefined);
  const unknown = getUnknownTemplateFields(`${values.subject}\n${values.text}`);
  const mutation = useMutation({
    mutationFn: async () => {
      if (!(await trigger()) || unknown.length)
        throw new Error("Проверьте название, тему и поля шаблона.");
      const current = getValues();
      const data = {
        name: current.name.trim(),
        subject: current.subject.trim(),
        text: current.text.trim(),
        attachments: validateMailAttachments(current.attachments ?? []),
      };
      const result = create
        ? await provider.create<MailTemplate>("email_templates", { data })
        : await provider.update<MailTemplate>("email_templates", {
            id: template!.id,
            data,
            previousData: template!,
          });
      return result.data;
    },
    onSuccess: (saved) => {
      dirty.current = false;
      client.invalidateQueries({ queryKey: ["email_templates"] });
      notify("Шаблон сохранён");
      onSaved(saved);
    },
  });
  useEffect(() => {
    dirty.current = formState.isDirty;
    busy.current = mutation.isPending || filesBusy;
  }, [formState.isDirty, mutation.isPending, filesBusy, dirty, busy]);
  useEffect(() => {
    if (!formState.isDirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [formState.isDirty]);
  const rendered = renderMailTemplate(
    { subject: values.subject || "", text: values.text || "" },
    sample ? TEMPLATE_PREVIEW_EXAMPLE : {},
  );
  const remember = (
    event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const input = event.currentTarget;
    const name = input.name === "subject" ? "subject" : "text";
    setField(name);
    insertion.current = {
      field: name,
      start: input.selectionStart ?? input.value.length,
      end: input.selectionEnd ?? input.value.length,
    };
  };
  const insert = (name: string) => {
    const value = getValues(field) || "";
    const position =
      insertion.current?.field === field
        ? insertion.current
        : { start: value.length, end: value.length };
    const token = `{{${name}}}`;
    setValue(
      field,
      value.slice(0, position.start) + token + value.slice(position.end),
      { shouldDirty: true },
    );
    insertion.current = {
      field,
      start: position.start + token.length,
      end: position.start + token.length,
    };
  };
  const nonBlank = (value: string) =>
    value?.trim() ? undefined : "Заполните поле";
  return (
    <div className="space-y-5">
      <div className="grid min-w-0 items-start gap-6 lg:grid-cols-2">
        <fieldset disabled={mutation.isPending} className="min-w-0 space-y-4">
          <TextInput
            source="name"
            label="Название шаблона"
            maxLength={100}
            validate={nonBlank}
            helperText="Видно только вам, клиент это название не получит."
          />
          <TextInput
            source="subject"
            label="Тема письма"
            maxLength={500}
            validate={nonBlank}
            onSelect={remember}
          />
          <TextInput
            source="text"
            label="Текст письма"
            multiline
            rows={14}
            maxLength={100_000}
            validate={nonBlank}
            onSelect={remember}
            inputClassName="leading-6"
          />
          <details className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Вставить поле в {field === "subject" ? "тему" : "текст"}
            </summary>
            <p className="my-2 text-xs leading-5 text-muted-foreground">
              Поставьте курсор в письме и выберите поле. Обращение уже включает
              запятую: «Здравствуйте{`{{contact_name}}`}!».
            </p>
            <div className="flex flex-wrap gap-1.5">
              {MAIL_TEMPLATE_VARIABLES.map((name) => (
                <Button
                  key={name}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => insert(name)}
                >
                  {TEMPLATE_FIELD_LABELS[name]}
                </Button>
              ))}
            </div>
          </details>
          {!!unknown.length && (
            <p role="alert" className="text-sm text-destructive">
              Неизвестные поля: {unknown.join(", ")}. Замените их полями из
              списка выше.
            </p>
          )}
          <p className="text-xs leading-5 text-muted-foreground">
            Шаблон хранит тему и текст. Для имён и меняющихся условий
            используйте поля подстановки. Файлы шаблона копируются в письмо.
            Сохранённые черновики не изменятся при замене файлов в шаблоне.
          </p>
          <MailAttachments
            value={values.attachments}
            label="Файлы шаблона"
            disabled={mutation.isPending}
            onBusyChange={setFilesBusy}
            onChange={(files: MailAttachment[]) =>
              setValue("attachments", files, { shouldDirty: true })
            }
          />
          <p className="text-xs leading-5 text-muted-foreground">
            Прикрепляйте общие материалы. Персональное КП для клиента можно
            добавить позже в его письмо.
          </p>
        </fieldset>
        <section className="min-w-0 space-y-3" aria-label="Живой предпросмотр">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={sample}
              onClick={() => setSample(!sample)}
            >
              {sample ? "С примерами данных" : "С названиями полей"}
            </Button>
            <PreviewSize compact={compact} onChange={setCompact} />
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            Вымышленные данные для просмотра. Они не сохраняются в шаблон и не
            переходят в письмо клиенту.
          </p>
          <EmailPreview
            {...rendered}
            from="Сергей <manager@example.org>"
            to="Анна <client@example.org>"
            compact={compact}
          />
        </section>
      </div>
      {mutation.error && (
        <p role="alert" className="text-sm text-destructive">
          {mutation.error.message}
        </p>
      )}
      <DialogFooter className="sticky bottom-0 border-t bg-background py-3">
        <Button
          type="button"
          variant="outline"
          disabled={mutation.isPending || filesBusy}
          onClick={onClose}
        >
          Отмена
        </Button>
        <Button
          type="button"
          disabled={mutation.isPending || filesBusy || !!unknown.length}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Сохранение…" : "Сохранить шаблон"}
        </Button>
      </DialogFooter>
    </div>
  );
};

const UseTemplateDialog = ({
  template,
  onClose,
}: {
  template: MailTemplate;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Для какой компании подготовить письмо?</DialogTitle>
          <DialogDescription>
            Шаблон «{template.name}». Дальше вы выберете адресата и заполните
            условия предложения.
          </DialogDescription>
        </DialogHeader>
        <Form
          className="space-y-4"
          onSubmit={(values: { companyId?: string | number }) => {
            if (values.companyId == null) return;
            navigate(
              `/companies/${encodeURIComponent(values.companyId)}/show/email?template=${encodeURIComponent(template.id)}`,
            );
          }}
        >
          <ReferenceInput
            source="companyId"
            reference="companies"
            sort={{ field: "name", order: "ASC" }}
          >
            <AutocompleteInput
              label="Компания"
              optionText="name"
              validate={required()}
            />
          </ReferenceInput>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit">
              Продолжить
              <ArrowRight />
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
